import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private idleTimeoutMs = 15 * 60 * 1000; // 15 minutes
  private absoluteTimeoutMs = 2 * 60 * 60 * 1000; // 2 hours
  private absoluteWarningMs = 5 * 60 * 1000; // 5 minutes warning before 2 hours

  private idleTimer: any;
  private absoluteTimer: any;
  private absoluteWarningTimer: any;
  private countdownTimer: any;

  private activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone,
  ) {
    this.handleStorageEvent = this.handleStorageEvent.bind(this);
  }

  startSessionMonitoring() {
    if (!this.authService.isAuthenticated()) return;

    // Set or refresh absolute session start
    const absoluteStart = localStorage.getItem('absoluteSessionStart');
    if (!absoluteStart) {
      localStorage.setItem('absoluteSessionStart', Date.now().toString());
    }

    this.updateLastActivity();
    this.setupActivityListeners();
    this.startTimers();

    window.addEventListener('storage', this.handleStorageEvent);
  }

  stopSessionMonitoring() {
    this.clearActivityListeners();
    this.clearAllTimers();
    window.removeEventListener('storage', this.handleStorageEvent);
    localStorage.removeItem('absoluteSessionStart');
    localStorage.removeItem('lastActivity');
  }

  private setupActivityListeners() {
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach((event) => {
        window.addEventListener(event, () => this.onUserActivity());
      });
    });
  }

  private clearActivityListeners() {
    this.activityEvents.forEach((event) => {
      window.removeEventListener(event, () => this.onUserActivity());
    });
  }

  private onUserActivity() {
    this.updateLastActivity();
  }

  private updateLastActivity() {
    const now = Date.now().toString();
    localStorage.setItem('lastActivity', now);
    this.ngZone.run(() => {
      this.resetIdleTimer();
    });
  }

  private startTimers() {
    this.resetIdleTimer();
    this.setupAbsoluteTimers();
  }

  private resetIdleTimer() {
    clearTimeout(this.idleTimer);
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    const elapsed = Date.now() - lastActivity;
    const remaining = Math.max(0, this.idleTimeoutMs - elapsed);

    this.idleTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.showIdleWarning();
      });
    }, remaining);
  }

  private setupAbsoluteTimers() {
    clearTimeout(this.absoluteTimer);
    clearTimeout(this.absoluteWarningTimer);

    const absoluteStart = parseInt(localStorage.getItem('absoluteSessionStart') || '0', 10);
    const elapsed = Date.now() - absoluteStart;

    const remainingToWarning = Math.max(0, this.absoluteTimeoutMs - this.absoluteWarningMs - elapsed);
    const remainingToExpiry = Math.max(0, this.absoluteTimeoutMs - elapsed);

    this.absoluteWarningTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.showAbsoluteWarning();
      });
    }, remainingToWarning);

    this.absoluteTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.expireSession('Your session has reached its maximum lifetime.');
      });
    }, remainingToExpiry);
  }

  private showIdleWarning() {
    let countdown = 60; // 60 seconds to extend

    Swal.fire({
      title: 'Are you still there?',
      html: 'Your session will expire in <b>' + countdown + '</b> seconds due to inactivity.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Extend Session',
      cancelButtonText: 'Logout Now',
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: 60000,
      timerProgressBar: true,
      didOpen: () => {
        this.countdownTimer = setInterval(() => {
          countdown--;
          const content = Swal.getHtmlContainer();
          if (content) {
            const b = content.querySelector('b');
            if (b) {
              b.textContent = countdown.toString();
            }
          }
        }, 1000);
      },
      willClose: () => {
        clearInterval(this.countdownTimer);
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.updateLastActivity();
      } else {
        this.logoutSession();
      }
    });
  }

  private showAbsoluteWarning() {
    Swal.fire({
      title: 'Session Expiring Soon',
      text: 'For security reasons, your session will expire in 5 minutes. Please save your work.',
      icon: 'warning',
      confirmButtonText: 'OK',
      timer: 300000,
      timerProgressBar: true,
    });
  }

  private expireSession(reason: string) {
    this.stopSessionMonitoring();
    this.authService.logout();
    localStorage.setItem('logout-event', Date.now().toString());

    Swal.fire({
      title: 'Session Expired',
      text: reason,
      icon: 'info',
      confirmButtonText: 'Login',
    });
  }

  private logoutSession() {
    this.stopSessionMonitoring();
    this.authService.logout();
    localStorage.setItem('logout-event', Date.now().toString());
  }

  private handleStorageEvent(event: StorageEvent) {
    if (event.key === 'lastActivity' && event.newValue) {
      this.ngZone.run(() => {
        this.resetIdleTimer();
      });
    } else if (event.key === 'logout-event') {
      this.ngZone.run(() => {
        this.stopSessionMonitoring();
        this.authService.logout();
      });
    }
  }

  private clearAllTimers() {
    clearTimeout(this.idleTimer);
    clearTimeout(this.absoluteTimer);
    clearTimeout(this.absoluteWarningTimer);
    clearInterval(this.countdownTimer);
  }
}
